import { QueryClient } from '@tanstack/react-query'

import { toAppError } from '@/lib/db/supabase'

/**
 * Query defaults tuned for an accounting app.
 *
 * The governing rule: **never show a stale number.** A cached bank balance that
 * disagrees with the ledger is worse than a spinner, so figures refetch on focus
 * and stale time is short. Master data (contacts, categories) changes rarely and
 * gets a longer stale time via per-query overrides.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Never retry a permission denial or a constraint violation — the
        // outcome is deterministic and retrying just delays the error message.
        if (!toAppError(error).retryable) return false
        return failureCount < 2
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },
    mutations: {
      // Money-moving writes are never retried automatically. A retry after an
      // ambiguous timeout can post the same voucher twice, and a duplicate
      // journal entry is far more expensive to find than a failed save.
      retry: false,
    },
  },
})

/**
 * Query key factory.
 *
 * Every key starts with the organization id so switching orgs cannot show the
 * previous org's cached figures, and so one org's data can be invalidated
 * without touching another's.
 */
export const queryKeys = {
  all: (orgId: string) => [orgId] as const,

  // Access control
  members: (orgId: string) => [orgId, 'members'] as const,
  invites: (orgId: string, includeHistory = false) =>
    [orgId, 'invites', includeHistory] as const,

  // Masters
  banks: (orgId: string) => [orgId, 'banks'] as const,
  bank: (orgId: string, id: string) => [orgId, 'banks', id] as const,
  contacts: (orgId: string) => [orgId, 'contacts'] as const,
  contact: (orgId: string, id: string) => [orgId, 'contacts', id] as const,
  categories: (orgId: string) => [orgId, 'categories'] as const,
  items: (orgId: string) => [orgId, 'items'] as const,
  products: (orgId: string) => [orgId, 'products'] as const,
  platforms: (orgId: string) => [orgId, 'platforms'] as const,
  taxRates: (orgId: string) => [orgId, 'tax-rates'] as const,
  accounts: (orgId: string) => [orgId, 'accounts'] as const,

  // Documents — `filters` is the serialised filter state, so each distinct view
  // caches separately.
  expenses: (orgId: string, filters?: unknown) => [orgId, 'expenses', filters ?? null] as const,
  incomes: (orgId: string, filters?: unknown) => [orgId, 'incomes', filters ?? null] as const,
  payments: (orgId: string, filters?: unknown) => [orgId, 'payments', filters ?? null] as const,
  transfers: (orgId: string, filters?: unknown) => [orgId, 'transfers', filters ?? null] as const,
  salesInvoices: (orgId: string, filters?: unknown) => [orgId, 'sales', filters ?? null] as const,
  purchaseBills: (orgId: string, filters?: unknown) =>
    [orgId, 'purchases', filters ?? null] as const,
  loans: (orgId: string, filters?: unknown) => [orgId, 'loans', filters ?? null] as const,
  investments: (orgId: string, filters?: unknown) =>
    [orgId, 'investments', filters ?? null] as const,
  journalVouchers: (orgId: string, filters?: unknown) =>
    [orgId, 'journal-vouchers', filters ?? null] as const,

  // Derived figures — everything below is computed from the ledger.
  ledger: (orgId: string) => [orgId, 'ledger'] as const,
  dashboard: (orgId: string) => [orgId, 'ledger', 'dashboard'] as const,
  bankBalances: (orgId: string, asOf: string) =>
    [orgId, 'ledger', 'bank-balances', asOf] as const,
  contactOutstanding: (orgId: string, asOf: string, kind: string) =>
    [orgId, 'ledger', 'contact-outstanding', asOf, kind] as const,
  categoryTotals: (orgId: string, from: string, to: string, kind: string) =>
    [orgId, 'ledger', 'category-totals', from, to, kind] as const,
  reports: (orgId: string, report: string, params?: unknown) =>
    [orgId, 'ledger', 'reports', report, params ?? null] as const,
} as const

/**
 * Invalidates everything a posted voucher can affect.
 *
 * Saving one expense changes the bank balance, the dashboard donut, the P&L, the
 * category report and the day book. Enumerating those by hand at every call site
 * guarantees one gets forgotten and a stale figure ships, so all derived data
 * hangs off the shared `[orgId, 'ledger']` prefix and is dropped in one call.
 *
 * `documentKey` additionally refreshes the list the user is looking at.
 */
export function invalidateAfterPosting(orgId: string, documentKey?: readonly unknown[]): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.ledger(orgId) })
  if (documentKey) void queryClient.invalidateQueries({ queryKey: documentKey })
}
